import type { InjectionKey, Ref } from 'vue'
import {
  AppointmentStatus,
  ConnectionType,
  ContainerType,
  ContainerWatchState,
  PortalName,
  PortComplex,
  ShippingLine,
  TerminalName,
  UserRoleType,
} from './services/apiClient'
import { ICONOGRAPHY } from './constants/iconography'

export const contentAreaHeightInjectionKey = Symbol(
  'containerAreaHeight'
) as InjectionKey<Ref<number>>

// Icon *names*, for the places that pick an icon at runtime — a container's
// status, a history entry's change type — and therefore render through
// `<Icon :icon="\u2026">` rather than a build-time `<i-mdi:\u2026>`. That is the one
// legitimate use of the runtime component; see docs/iconography.md.
//
// Every name that has a registered concept is read from the registry rather
// than repeated here, so the status column and the icon components cannot
// drift. They had: in/out-gate were `ion:enter-outline`/`ion:exit-outline`
// here and `mdi:login`/`mdi:logout` in InGateIcon/OutGateIcon, so one gate
// move wore two different icons depending on which surface drew it. Worse,
// `ion` and `carbon` are not in ENABLED_ICON_COLLECTIONS at all — they only
// resolved because the Iconify API served them at runtime, and any build-time
// use of the same name would have silently rendered nothing.
export const NEEDS_APPT_ICON = ICONOGRAPHY.needsAppointment.name
export const APPT_ICON = ICONOGRAPHY.appointment.name
export const ADD_APPT_ICON = ICONOGRAPHY.bookAppointment.name
export const CONTAINER_ICON = ICONOGRAPHY.containerEmpty.name
export const EMPTY_CONTAINER_ICON = ICONOGRAPHY.containerEmpty.name
export const LOADED_CONTAINER_ICON = ICONOGRAPHY.containerLoaded.name
export const ON_VESSEL_ICON = ICONOGRAPHY.vessel.name
export const OUTGATE_ICON = ICONOGRAPHY.outGate.name
export const INGATE_ICON = ICONOGRAPHY.inGate.name

// No registered concept: neither is a domain idea, they are chrome that
// happens to be needed as a string. `UNKNOWN_ICON` was `carbon:unknown`,
// swapped for an MDI equivalent so it comes from an installed collection.
export const EDIT_APPT_ICON = 'mdi:calendar-edit'
export const UNKNOWN_ICON = 'mdi:help-circle-outline'

// Deliberately still its own literal: this marks a container that *cannot yet*
// be booked, which is not the same as cancelling an appointment — but it is
// the same glyph as `cancelAppointment`. Left alone because changing it moves
// a status icon users already read; noted in docs/iconography.md instead.
export const CANT_MAKE_APPT_ICON = 'mdi:calendar-remove'

export const DEMO_MODE = true && import.meta.env.DEV

export const EMPTY_DIALOG_WIDTH = '700px'

// The dedicated UTAS app. It runs on a separate origin from the main app, so its
// localStorage/auth is NOT shared with app.draydog.com.
export const UAS_APP_URL = 'https://uas.draydog.com'

// Hardcoded until /terminals/config/ API is available (DEV-6227)
export const BULK_BOOKING_LIMIT = 10

export interface ContainerStatusFilter {
  label: string
  icon: string
  states: ContainerWatchState[]
}
export const AVAILABLE_STATUS_LABEL = 'Bookable'

export function getContainerStateIcon(state: ContainerWatchState): string {
  switch (state) {
    case ContainerWatchState.ImportTrackingOnVessel:
      return ON_VESSEL_ICON
    case ContainerWatchState.ImportTracking:
      return CANT_MAKE_APPT_ICON
    case ContainerWatchState.ImportApptBooking:
      return NEEDS_APPT_ICON
    case ContainerWatchState.ImportApptBooked:
      return APPT_ICON
    case ContainerWatchState.ImportPickedUp:
      return OUTGATE_ICON
    case ContainerWatchState.ImportEmpty:
      return EMPTY_CONTAINER_ICON
    case ContainerWatchState.ImportEmptyApptBooked:
      return APPT_ICON
    case ContainerWatchState.ImportEmptyReturned:
      return INGATE_ICON
    case ContainerWatchState.ExportApptBooked:
      return APPT_ICON
    default:
      return UNKNOWN_ICON
  }
}

export function getContainerStateLabel(state: ContainerWatchState): string {
  switch (state) {
    case ContainerWatchState.ImportTrackingOnVessel:
      return 'On Vessel'
    case ContainerWatchState.ImportTracking:
      return 'Unavailable'
    case ContainerWatchState.ImportApptBooking:
      return AVAILABLE_STATUS_LABEL
    case ContainerWatchState.ImportApptBooked:
      return 'Appointed'
    case ContainerWatchState.ImportPickedUp:
      return 'Out-Gated'
    case ContainerWatchState.ImportEmpty:
      return 'Empty'
    case ContainerWatchState.ImportEmptyApptBooked:
      return 'Empty Appointed'
    case ContainerWatchState.ImportEmptyReturned:
      return 'Terminated'
    case ContainerWatchState.Unknown:
      return 'Unknown Status'
    case ContainerWatchState.ExportApptBooked:
      return 'Export Appointment Booked'
    default:
      return state
  }
}

export const CONTAINER_STATES: ContainerStatusFilter[] = [
  {
    label: 'On Vessel',
    icon: ON_VESSEL_ICON,
    states: [ContainerWatchState.ImportTrackingOnVessel],
  },
  {
    label: 'Unavailable',
    icon: CANT_MAKE_APPT_ICON,
    states: [ContainerWatchState.ImportTracking],
  },
  {
    label: AVAILABLE_STATUS_LABEL,
    icon: NEEDS_APPT_ICON,
    states: [ContainerWatchState.ImportApptBooking],
  },
  {
    label: 'Appointed',
    icon: APPT_ICON,
    states: [
      ContainerWatchState.ImportApptBooked,
      ContainerWatchState.ImportEmptyApptBooked,
    ],
  },
  {
    label: 'Out-Gated',
    icon: OUTGATE_ICON,
    // ImportEmpty (marked empty, no empty-return appt booked yet) shares this tab with
    // ImportPickedUp so containers stay findable after "Mark Empty" — otherwise they fall
    // into no tab and vanish from the Imports list and its counts (DEV: Living Spaces).
    states: [
      ContainerWatchState.ImportPickedUp,
      ContainerWatchState.ImportEmpty,
    ],
  },
  {
    label: 'Terminated',
    icon: INGATE_ICON,
    states: [ContainerWatchState.ImportEmptyReturned],
  },
  {
    label: 'Unknown',
    icon: UNKNOWN_ICON,
    states: [ContainerWatchState.Unknown],
  },
]

export const CONTAINER_DISPLAY_STATES_BY_STATE = new Map() as Map<
  ContainerWatchState,
  ContainerStatusFilter
>
for (const entry of CONTAINER_STATES) {
  for (const state of entry.states)
    CONTAINER_DISPLAY_STATES_BY_STATE.set(state, entry)
}

export const LA_LB_TERMINALS: { name: TerminalName; label: string }[] = [
  { name: TerminalName.Apm, label: 'APM' },
  { name: TerminalName.Everport, label: 'Everport' },
  { name: TerminalName.Fms, label: 'FMS' },
  { name: TerminalName.Lbct, label: 'LBCT' },
  // { name: TerminalName.MatsonLb, label: "Matson" },

  { name: TerminalName.Pct, label: 'PCT' },
  { name: TerminalName.PierA, label: 'Pier A' },
  { name: TerminalName.Trapac, label: 'Trapac' },
  { name: TerminalName.Tti, label: 'TTI' },
  { name: TerminalName.Wbct, label: 'WBCT' },
  { name: TerminalName.Yti, label: 'YTI' },
  { name: TerminalName.Its, label: 'ITS' },
  { name: TerminalName.MatsonLb, label: 'Matson (C60)' },
  { name: TerminalName.StAlam, label: 'Shippers Alameda' },
  { name: TerminalName.StLb, label: 'Shippers Long Beach' },
  { name: TerminalName.StCarson, label: 'Shippers Carson' },
]

export const OAKLAND_TERMINALS: { name: TerminalName; label: string }[] = [
  { name: TerminalName.EverportOak, label: 'Everport (Oak)' },
  { name: TerminalName.TrapacOak, label: 'Trapac (Oak)' },
  { name: TerminalName.Oict, label: 'OICT (Oak)' },
]

export const PORT_LABELS: { name: PortComplex; label: string }[] = [
  { name: PortComplex.LaLb, label: 'LA/LB' },
  { name: PortComplex.Oak, label: 'Oakland' },
]
const PORT_LABEL_MAP = new Map<PortComplex, string>(
  PORT_LABELS.map((p) => [p.name, p.label])
)

export function getPortLabel(port: PortComplex): string {
  return PORT_LABEL_MAP.get(port) || port
}
export const OAKLAND_TERMINALS_SET = new Set(
  OAKLAND_TERMINALS.map((t) => t.name)
)
export const LA_LB_TERMINALS_SET = new Set(LA_LB_TERMINALS.map((t) => t.name))

export const PORT_COMPLEX_TERMINALS: {
  name: PortComplex
  terminals: Set<TerminalName>
}[] = [
  {
    name: PortComplex.LaLb,
    terminals: new Set(LA_LB_TERMINALS.map((t) => t.name)),
  },
  {
    name: PortComplex.Oak,
    terminals: new Set(OAKLAND_TERMINALS.map((t) => t.name)),
  },
]

export const TERMINAL_PORT_COMPLEX_MAP = new Map<TerminalName, PortComplex>(
  PORT_COMPLEX_TERMINALS.flatMap((p) =>
    Array.from(p.terminals).map(
      (terminal) => [terminal, p.name] as [TerminalName, PortComplex]
    )
  )
)

export const APPOINTMENT_STATUSES: {
  name: AppointmentStatus
  label: string
}[] = [
  { name: AppointmentStatus.Scheduled, label: 'Scheduled' },
  { name: AppointmentStatus.InProgress, label: 'In Progress' },
  { name: AppointmentStatus.Completed, label: 'Completed' },
  { name: AppointmentStatus.Missed, label: 'Missed' },
  { name: AppointmentStatus.Cancelled, label: 'Cancelled' },
  { name: AppointmentStatus.Aborted, label: 'Aborted' },
  { name: AppointmentStatus.SchedulingError, label: 'Scheduling Error' },
]

export const ALL_TERMINALS = new Set([
  ...LA_LB_TERMINALS_SET,
  ...OAKLAND_TERMINALS_SET,
])
const all_statuses = new Set(APPOINTMENT_STATUSES.map((s) => s.name))

export const TERMINALS_REQUIRING_MASTER_BL = new Set<TerminalName>([
  // TerminalName.Everport, // We pre-fill "internally"
  // TerminalName.Its, // We pre-fill "internally"
  // TerminalName.Fms, // We pre-fill "internally"
  TerminalName.Wbct,
])

export const TERMINALS_WHERE_LICENSE_PLATE_CAN_BE_EDITED =
  new Set<TerminalName>([
    TerminalName.Apm,
    TerminalName.Pct,
    TerminalName.PierA,
    TerminalName.Its,
    TerminalName.Everport,
    TerminalName.Lbct,
    TerminalName.Fms,
    TerminalName.Trapac,
    // TTI can be edited because it uses eModal now
    TerminalName.Tti,
    TerminalName.Wbct,
    TerminalName.Oict,
    TerminalName.EverportOak,
    TerminalName.TrapacOak,
  ])

export const TERMINALS_REQUIRING_APPOINTMENT_CHECK_IN = new Set<TerminalName>([
  TerminalName.Its,
  TerminalName.Pct,
  TerminalName.PierA,
])
export const TERMINALS_REQUIRING_APPOINTMENT_CHECK_IN_FOR_INBOUND_APPOINTMENTS =
  new Set<TerminalName>([TerminalName.Trapac])
export const TERMINAL_NOT_REQUIRING_APPOINTMENTS = new Set<TerminalName>([
  TerminalName.StAlam,
  TerminalName.StLb,
  TerminalName.StCarson,
  TerminalName.MatsonLb,
])
export const TERMINALS_REQUIRING_APPOINTMENTS = new Set<TerminalName>(
  Array.from(ALL_TERMINALS).filter(
    (terminal) => !TERMINAL_NOT_REQUIRING_APPOINTMENTS.has(terminal)
  )
)

export const STATUSES_REQUIRING_APPOINTMENTS = Array.from(all_statuses)

export const TERMINALS_REQUIRING_LICENSE_PLATE = new Set<TerminalName>([
  // TerminalName.Its, # ITS longer requires license plate
  TerminalName.Trapac,
])
export const TERMINALS_REQUIRING_LICENSE_PLATE_FOR_EMPTY_APPOINTMENT =
  // TerminalName.Its, # eModal accepts plateless empties at ITS (DEV-7829),
  // mirroring the removal from TERMINALS_REQUIRING_LICENSE_PLATE above
  new Set<TerminalName>([TerminalName.Trapac])
export const TERMINALS_WHERE_LICENSE_PLATE_IS_NOT_USED_WHEN_BOOKING =
  new Set<TerminalName>([TerminalName.Wbct, TerminalName.Yti])
export const TERMINALS_WHERE_LICENSE_PLATE_IS_NOT_USED_WHEN_BOOKING_EMPTY_APPOINTMENTS =
  new Set<TerminalName>([TerminalName.Wbct, TerminalName.Fms, TerminalName.Yti])

export const TERMINALS_REQUIRING_LICENSE_PLATE_TO_COMPLETE_IMPORT_APPOINTMENT =
  new Set<TerminalName>([
    TerminalName.Apm,
    TerminalName.Trapac,
    TerminalName.TrapacOak,
    TerminalName.Pct,
    TerminalName.PierA,
    TerminalName.Its,
  ])

export const SHIPPERS_TERMINALS = new Set<TerminalName>([
  TerminalName.StAlam,
  TerminalName.StLb,
  TerminalName.StCarson,
])

export const EMODAL_TERMINALS = new Set<TerminalName>([
  TerminalName.Everport,
  TerminalName.Trapac,
  TerminalName.Pct,
  TerminalName.PierA,
  TerminalName.Its,
  TerminalName.MatsonLb,
  TerminalName.Tti,
  TerminalName.Oict,
  TerminalName.EverportOak,
  TerminalName.TrapacOak,
])
// In the future we could pull these chassis options from the values defined in the
// backend (see dd_constants)
export const TERMINALS_WITH_OWN_CHASSIS_OPTION_FOR_IMPORT_APPT =
  new Set<TerminalName>([TerminalName.Apm, TerminalName.Its, TerminalName.Wbct])

export const TERMINALS_WITH_OWN_CHASSIS_SELECTOR_SINGLE_EMPTY_APPT =
  new Set<TerminalName>([
    TerminalName.Apm,
    TerminalName.Its,
    TerminalName.Wbct,
    TerminalName.Trapac,
    TerminalName.Tti,
    TerminalName.Pct,
    TerminalName.PierA,
    TerminalName.Everport,
    TerminalName.Oict,
    TerminalName.TrapacOak,
    TerminalName.EverportOak,
  ])
export const TERMINALS_WITH_CHASSIS_NUMBER_INPUT_FOR_POOL_CHASSIS_EMPTY_APPT =
  new Set<TerminalName>([
    TerminalName.Pct,
    TerminalName.PierA,
    TerminalName.Everport,
    TerminalName.Oict,
    TerminalName.TrapacOak,
    TerminalName.EverportOak,
  ])

export const TERMINALS_WHERE_CHASSIS_NUMBER_IS_REQUIRED_FOR_POOL_CHASSIS_EMPTY_APPT =
  new Set<TerminalName>([
    TerminalName.EverportOak,
    TerminalName.TrapacOak,
    TerminalName.Pct,
    TerminalName.PierA,
    TerminalName.Everport,
  ])
export const TERMINALS_WITH_CHASSIS_NUMBER_OPTION_FOR_ADD_EMPTY =
  new Set<TerminalName>([
    TerminalName.Everport,
    TerminalName.Oict,
    TerminalName.TrapacOak,
    TerminalName.EverportOak,
  ])
export const TERMINALS_WITH_CHASSIS_NUMBER_OPTION_FOR_SWAP_EMPTY =
  new Set<TerminalName>([TerminalName.Everport])
export const TERMINALS_TO_ALLOW_AUTOMATIC_PLATE_SETTING = new Set<TerminalName>(
  [TerminalName.Apm]
)
/**
 * Terminals running a green lane / clean truck program, where an appointment booked on
 * a green/EV plate can't be moved to a non-green truck. Mirrors the backend's
 * `has_green_lane` terminal policy (`dd/logic/terminal_policies.py`).
 */
export const TERMINALS_WITH_GREEN_LANE = new Set<TerminalName>([
  TerminalName.Apm,
])
export const TERMINALS_WHERE_LICENSE_PLATE_IS_OPTIONAL_USED_WHEN_BOOKING =
  new Set<TerminalName>()
for (const terminal of ALL_TERMINALS) {
  if (
    !TERMINALS_WHERE_LICENSE_PLATE_IS_NOT_USED_WHEN_BOOKING.has(terminal) &&
    !TERMINALS_REQUIRING_LICENSE_PLATE.has(terminal) &&
    !TERMINAL_NOT_REQUIRING_APPOINTMENTS.has(terminal)
  ) {
    TERMINALS_WHERE_LICENSE_PLATE_IS_OPTIONAL_USED_WHEN_BOOKING.add(terminal)
  }
}

export const TERMINALS_NOT_REQUIRING_EMPTY_APPOINTMENTS_FOR_DUALS =
  new Set<TerminalName>([TerminalName.Trapac, TerminalName.Yti])

const TERMINAL_LOOKUP = new Map<TerminalName, string>([
  ...LA_LB_TERMINALS.map((t) => [t.name, t.label] as [TerminalName, string]),
  ...OAKLAND_TERMINALS.map((t) => [t.name, t.label] as [TerminalName, string]),
])
const STATUS_LOOKUP = new Map<AppointmentStatus, string>(
  APPOINTMENT_STATUSES.map(
    (s) => [s.name, s.label] as [AppointmentStatus, string]
  )
)
export function getTerminalLabel(terminal: TerminalName): string {
  return TERMINAL_LOOKUP.get(terminal) || terminal
}
export function getStatusLabel(status: AppointmentStatus): string {
  return STATUS_LOOKUP.get(status) || status
}
const PORTAL_NAMES = new Map<PortalName, string>([
  [PortalName.Apm, 'APM'],
  [PortalName.ApmEir, 'APM EIR'],
  [PortalName.ApmTops, 'APM TOPS'],
  [PortalName.ApmTermpoint, 'APM Termpoint'],
  [PortalName.ApmTermpointApi, 'APM Termpoint API'],
  [PortalName.Emodal, 'eModal'],
  [PortalName.EmodalAsb, 'eModal Real-Time'],
  [PortalName.Everport, 'Everport'],
  [PortalName.FmsCommunityPortal, 'FMS Community Portal'],
  [PortalName.FmsVoyageControls, 'FMS Voyage Controls'],
  [PortalName.FmsGoogle, 'FMS'],
  [PortalName.Lbct, 'LBCT'],
  [PortalName.LbctApi, 'LBCT'],
  [PortalName.PctTideworks, 'PCT'],
  [PortalName.PierATideworks, 'Pier A'],
  [PortalName.Trapac, 'Trapac'],
  [PortalName.Tti, 'TTI'],
  [PortalName.TtiNoLogin, 'TTI'],
  [PortalName.Wbct, 'WBCT'],
  [PortalName.Yti, 'YTI'],
  [PortalName.YtiLynx, 'YTI Lynx'],
  [PortalName.YtiVoyageControls, 'YTI Voyage Controls'],
  [PortalName.YtiGateLink, 'YTI Gate Link'],
  [PortalName.Its, 'ITS'],
  [PortalName.ItsApi, 'ITS API'],
  [PortalName.ItsNoLogin, 'ITS'],
  [PortalName.ShippersExpressTideworks, 'Shippers Express'],
  [PortalName.ShippersLongbeachTideworks, 'Shippers Long Beach'],
  [PortalName.PortOptimizer, 'Port Optimizer'],
  [PortalName.PortOptimizerApi, 'Port Optimizer API'],
  [PortalName.FmsApi, 'FMS API'],
  [PortalName.TrapacApi, 'TraPac'],
  [PortalName.WbctApi, 'WBCT API'],
  [PortalName.OictTideworks, 'OICT'],
])
export function getPortalLabel(portal: PortalName): string {
  return PORTAL_NAMES.get(portal) || portal
}
export function isInternalUser(email: string): boolean {
  return (
    email.endsWith('@draydog.com') ||
    email.endsWith('@wabtec.com') ||
    email.endsWith('@cargosprint.com') ||
    email.endsWith('@portla.org') ||
    // Temporary hard-code, we really need to use the "is_dray_dog_admin" user field but
    // that needs to be added to the API first and I want to think on doing that
    email === 'alectrocute@gmail.com'
  )
}
export const TMS_CONNECTION_TYPES = [
  {
    type: ConnectionType.TriniumSftp,
    label: 'Trinium SFTP',
  },
  {
    type: ConnectionType.BlackflyConnect,
    label: 'Profit Tools',
  },
]

const CONTAINER_TYPE_DESCRIPTIONS = new Map<ContainerType, string>([
  [ContainerType._40Hc, '40ft High Cube'],
  [ContainerType._40Sd, '40ft Standard'],
  [ContainerType._45Hc, '45ft High Cube'],
  [ContainerType._20Sd, '20ft Standard'],
  [ContainerType._20HcReefer, '20ft Reefer'],
  [ContainerType._40HcReefer, '40ft Reefer'],
  [ContainerType._45HcReefer, '45ft Reefer'],
  [ContainerType._45Ot, '45ft Open Top'],
])

export function getContainerTypeDescription(
  containerType: ContainerType
): string {
  return CONTAINER_TYPE_DESCRIPTIONS.get(containerType) || containerType
}

export const NORMAL_USER_ROLES = [
  UserRoleType.CustomerService,
  UserRoleType.Dispatch,
  UserRoleType.Management,
  UserRoleType.Other,
]

// These are lines that might still show up in data/are useful historically but
// are no longer active
export const DEFUNCT_SHIPPING_LINES = new Set<ShippingLine>([
  ShippingLine.Aplu,
  ShippingLine.Banr,
  ShippingLine.Llhp,
  // ShippingLine.Sqqy,
  ShippingLine.Tgr,
  ShippingLine.Ymlu, // Replaced by Ymja
])

export function isValidShippingLine(value: any): value is ShippingLine {
  return Object.values(ShippingLine).includes(value)
}
export function isValidContainerType(value: any): value is ContainerType {
  return Object.values(ContainerType).includes(value)
}
export function isValidTerminalName(value: any): value is TerminalName {
  return Object.values(TerminalName).includes(value)
}

export const TERMS_AND_CONDITIONS_LINK = '/terms_of_service.pdf'
export const PRIVACY_POLICY_LINK = '/privacy_policy.pdf'

export const CONTAINER_ROW_MIN_WIDTH_PIXELS = 570
export const TRUCK_STATUS_DAYS_TO_LOAD = 30

export function getAppointmentSystemConnectionTypes(
  terminal: TerminalName
): ConnectionType[] | null {
  // Multiple types return when there are multiple suitable options for a terminal
  switch (terminal) {
    case TerminalName.StAlam:
    case TerminalName.StLb:
    case TerminalName.StCarson:
    case TerminalName.MatsonLb:
      return null
    case TerminalName.Wbct:
      return [ConnectionType.Wbct]
    case TerminalName.Apm:
      return [ConnectionType.ApmTermpoint, ConnectionType.ApmTermpointApi]
    case TerminalName.Yti:
      return [ConnectionType.YtiGateLink]
    case TerminalName.Fms:
      return [ConnectionType.FmsCommunityPortal]
    case TerminalName.Lbct:
      return [ConnectionType.LbctApi]
    case TerminalName.Tti:
    case TerminalName.Pct:
    case TerminalName.Its:
    case TerminalName.Everport:
    case TerminalName.PierA:
    case TerminalName.Trapac:
      return [ConnectionType.Emodal]
    case TerminalName.Oict:
      return [ConnectionType.OictTideworks]
    default:
      return null
  }
}

export const VESSEL_SCHEDULE_LINKS_BY_TERMINAL = new Map<TerminalName, string>([
  [
    TerminalName.Apm,
    'https://www.apmterminals.com/en/los-angeles/online-services/vessel-schedule',
  ],
  [TerminalName.Yti, 'https://yti.com/terminal-schedules/'],
  // Technically we get data from WBCT API, hopefully their website matches
  [
    TerminalName.Wbct,
    'https://tosportal.portsamerica.com/logon?siteId=WBCT_LA',
  ],
  // Technically we get data from WBCT API, hopefully their website matches
  [TerminalName.Trapac, 'https://losangeles.trapac.com/vessel-schedule/'],
  // Need to login and schedule is on main page in "Vessel Status" section
  [TerminalName.Fms, 'https://portal.fenixmarineservices.com/'],
  [TerminalName.Tti, 'https://www.totalterminals.com/vessel-schedule'],
  // Must login
  [TerminalName.Pct, 'https://pct.tideworks.com/fc-PCT/default.do'],
  // Must login
  [TerminalName.PierA, 'https://piera.tideworks.com/fc-PA/default.do'],
  [TerminalName.Its, 'https://www.itslb.com/vessel-cutoff-2/'],
  // Must login
  [TerminalName.Everport, 'https://www.etslink.com/'],
  [TerminalName.Lbct, 'https://portal.lbct.com/Operations/VesselSchedule'],
])

export const IMPORT_SLOTS_WEBSOCKET_PREFIX = 'private-import-slots-'
export const NEW_FEATURE_CONFIRMED_KEY_PREFIX = 'new-feature-confirmed-'
// Lives here (not in the globalNotifications store) because the user store
// also needs it for its localStorage keep-sets, and importing the store there
// would create a circular import
export const ACKNOWLEDGED_POPUPS_KEY = 'acknowledged_global_notification_popups'

// Layout constants
export const LAYOUT_DRAWER_CONTAINER_ID = 'layout-drawer-container'

/**
 * Ceiling on how many views a virtual scroller may render before it decides it has lost
 * its scroll boundary and bails out. vue-virtual-scroller defaults to 1000; this project
 * has always run at 5000.
 *
 * Through v2 this was set once globally via the plugin (`app.use(VueVirtualScroller, …)`).
 * v3 dropped `itemsLimit` from the plugin's `PluginOptions` type — the runtime still copies
 * it into the global config, but the documented API is now a per-scroller `items-limit`
 * prop, so we pass it explicitly on each scroller rather than rely on an untyped option.
 *
 * **This needs vue-virtual-scroller >= 3.0.5, and that is why the range is `^3.0.5`.** The
 * prop was added in 3.0.5; on 3.0.4 `RecycleScroller` has no such prop, so `items-limit`
 * degrades to an inert DOM attribute and — with the plugin option gone — the scroller
 * silently falls back to the library's default of 1000. Nothing catches that: an unknown
 * attribute is legal fallthrough, so it typechecks and every suite stays green. Don't let a
 * lockfile edit or a resolution override drop below 3.0.5.
 */
export const VIRTUAL_SCROLLER_ITEMS_LIMIT = 5000
